import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useSessionAttendance } from '@/hooks/use-attendance';
import { GroupSessionView, PrivateSessionView, ClinicSessionView, MatchPlaySessionView, CardioTennisSessionView, SquadsSessionView } from '@/components/coach/session-views';

import { formatTime } from '@/lib/format';

function capitalise(str: string): string {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AttendancePage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  if (!sessionId) {
    return <div className="p-6">Invalid session ID</div>;
  }

  const { data, isLoading } = useSessionAttendance(sessionId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Session not found</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  const sessionType = data.session.sessionType ?? 'group';

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-foreground">Session Attendance</h2>
              <p className="text-sm text-muted-foreground">
                {formatTime(data.session.startTime)} &mdash;{' '}
                {capitalise(sessionType)} Session
                {data.session.courtNumber != null && `; Court ${data.session.courtNumber}`}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-foreground">
                {data.presentCount}/{data.totalCount}
              </div>
              <div className="text-sm text-muted-foreground">Present</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Type-specific session view */}
      {sessionType === 'group' && <GroupSessionView sessionId={sessionId} />}
      {sessionType === 'private' && <PrivateSessionView sessionId={sessionId} />}
      {sessionType === 'clinic' && <ClinicSessionView sessionId={sessionId} />}
      {sessionType === 'match_play' && <MatchPlaySessionView sessionId={sessionId} sessionDate={data.session.date} />}
      {sessionType === 'cardio_tennis' && <CardioTennisSessionView sessionId={sessionId} />}
      {sessionType === 'squads' && <SquadsSessionView sessionId={sessionId} sessionDate={data.session.date} />}
    </div>
  );
}
